/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: RoleProfilesPage.tsx
 ??? Created: 2025-01-05
 ?? Description:
    Main role profiles management page with data grid, CRUD operations,
    and comprehensive role-based access control.

 ?? Notes:
    - Refactored to use modular hooks and components
    - Integrates DataGridSimple for role profile listing and management
    - Supports role profile creation, editing, deletion, and duplication
    - Implements search and filtering functionality
    - Uses confirmation modals for destructive actions

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Shield } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModulePageContainer } from '../../components/layouts';
import { deleteRoleProfileById, navigateToRoleProfileDuplicate } from './roleProfilesActions';
import { useRoleProfilesList } from './hooks';
import { useRoleProfilesColumnRenderers } from './components/RoleProfilesColumns';
import { RoleProfilesActions } from './components/RoleProfilesActions';
import { RoleProfile } from './types';

export default function RoleProfilesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();
  const tenantSlug = user?.tenant?.slug || t('profiles.defaultTenant');
  
  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<RoleProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear page title to show tenant logo + name in header
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Use hooks for data grid (includes columns internally)
  const {
    data: profiles,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useRoleProfilesList();

  // Column renderers
  const { getColumnRenderer } = useRoleProfilesColumnRenderers();

  // Handle profile form
  const handleCreateProfile = useCallback(() => {
    navigate('/role-profiles/new');
  }, [navigate]);

  const handleEditProfile = useCallback((profile: RoleProfile) => {
    navigate(`/role-profiles/${profile.uuid ?? profile.id}`);
  }, [navigate]);

  const handleDuplicateProfile = useCallback((profile: RoleProfile) => {
    navigateToRoleProfileDuplicate(navigate, profile.uuid ?? profile.id);
  }, [navigate]);

  const handleDeleteProfile = useCallback((profile: RoleProfile) => {
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!profileToDelete) return;

    // Check if profile has assigned users
    if (profileToDelete.usersCount > 0) {
      showError(t('profiles.messages.deleteErrorUsersAssigned'));
      setShowDeleteModal(false);
      setProfileToDelete(null);
      return;
    }

    try {
      setIsDeleting(true);
      await deleteRoleProfileById(profileToDelete.id, tenantSlug, user?.id);
      showSuccess(t('profiles.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setProfileToDelete(null);
      refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('profiles.messages.deleteError'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [profileToDelete, tenantSlug, user?.id, showSuccess, showError, refresh, t]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setProfileToDelete(null);
    setIsDeleting(false);
  }, []);

  // Render actions
  const renderActions = useCallback(
    (profile: RoleProfile) => (
      <RoleProfilesActions
        profile={profile}
        onEdit={handleEditProfile}
        onDuplicate={handleDuplicateProfile}
        onDelete={handleDeleteProfile}
      />
    ),
    [handleEditProfile, handleDuplicateProfile, handleDeleteProfile],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            titleIcon={<Shield className="h-5 w-5 text-primary" />}
            data={profiles}
            loading={loading}
            columns={columns.map((col) => ({
              ...col,
              render: getColumnRenderer(col.key),
            }))}
            onColumnToggle={handleColumnToggle}
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            searchQuery={searchQuery}
            onSearch={handleSearch}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onAdd={handleCreateProfile}
            onEdit={handleEditProfile}
            onDuplicate={handleDuplicateProfile}
            onDelete={handleDeleteProfile}
            title={t('profiles.title')}
            addButtonText={t('common.add')}
            searchPlaceholder={t('profiles.search')}
            emptyMessage={t('profiles.noProfiles')}
            deleteTitle={t('profiles.delete')}
            deleteMessage={(item) => {
              if (!item || !item.name) {
                return t('profiles.confirmDelete');
              }
              return t('profiles.confirmDelete');
            }}
            renderActions={renderActions}
            translations={{
              items: t('common:items'),
              columns: t('common:columns'),
              noItemsFound: t('common:noItemsFound'),
              showing: t('common:showing'),
              to: t('common:to'),
              of: t('common:of'),
              results: t('common:results'),
              previous: t('common:previous'),
              next: t('common:next'),
              page: t('common:page'),
              deleteItem: t('common:deleteItem'),
              areYouSureDeleteItem: t('common:areYouSureDeleteItem'),
              delete: t('common:delete'),
              cancel: t('common:cancel'),
              allSelected: t('common:allSelected'),
              selected: t('common:selected'),
              searchOptions: t('common:searchOptions'),
              noOptionsFound: t('common:noOptionsFound'),
              selectAll: t('common:selectAll'),
              clearAll: t('common:clearAll')
            }}
          />

          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={cancelDelete}
            onConfirm={confirmDelete}
            title={t('profiles.delete')}
            message={t('profiles.confirmDelete')}
            confirmText={t('profiles.delete')}
            cancelText={t('profiles.cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}
